import type { GameState, SeatPlayer, PlayerAction, PlayerActionType } from '@/types';
import { 
  calculateHandStrength, 
  calculatePotOdds, 
  calculatePositionAdvantage,
  calculatePreflopStrength 
} from './hand-strength';

export class MediumAI {
  private aggressionFactor: number = 0.7;

  decide(state: GameState, playerId: string): PlayerAction {
    const player = state.players.find(p => p.id === playerId);
    if (!player || !player.holeCards) {
      return { type: 'fold' };
    }

    const handStrength = state.communityCards.length === 0
      ? calculatePreflopStrength(player.holeCards)
      : calculateHandStrength(player.holeCards, state.communityCards);

    const validActions = this.getValidActions(state, player);
    const potSize = state.pots.reduce((sum, p) => sum + p.amount, 0);
    const toCall = state.currentBet - player.currentBet;
    const potOdds = calculatePotOdds(potSize, toCall);
    const position = calculatePositionAdvantage(state, playerId);

    // 计算期望值
    const expectedValue = this.calculateExpectedValue(
      handStrength, 
      potOdds, 
      position,
      toCall,
      player.chips
    );

    // 决策逻辑
    if (expectedValue > 0.6) {
      // 强牌或有利情况：加注
      if (validActions.includes('raise')) {
        const raiseAmount = this.calculateRaiseAmount(state, player, handStrength, potSize);
        return { type: 'raise', amount: raiseAmount };
      }
      if (validActions.includes('all-in')) {
        return { type: 'all-in' };
      }
    }

    if (expectedValue > 0.3) {
      // 中等牌：跟注或检查
      if (validActions.includes('call') && potOdds < handStrength * 1.2) {
        return { type: 'call' };
      }
      if (validActions.includes('check')) {
        return { type: 'check' };
      }
    }

    // 弱牌或不利情况
    if (validActions.includes('check')) {
      return { type: 'check' };
    }
    
    // 如果底池赔率很好，即使牌弱也跟注
    if (validActions.includes('call') && potOdds < handStrength * 0.8) {
      return { type: 'call' };
    }

    return { type: 'fold' };
  }

  private calculateExpectedValue(
    handStrength: number,
    potOdds: number,
    position: number,
    toCall: number,
    chips: number
  ): number {
    // 基础期望值
    let ev = handStrength;

    // 位置加成
    ev += position * 0.1;

    // 底池赔率加成
    if (potOdds < handStrength) {
      ev += 0.15;
    }

    // 筹码深度考虑
    const stackToPot = chips / (toCall + 1);
    if (stackToPot > 10) {
      ev += 0.05; // 深筹码可以玩得更松
    }

    return Math.min(ev, 1);
  }

  private calculateRaiseAmount(
    state: GameState, 
    player: SeatPlayer, 
    handStrength: number,
    potSize: number
  ): number {
    // 基于手牌强度和底池大小计算加注额
    const baseRaise = state.currentBet + state.minRaise;
    
    // 强牌加注更大
    const strengthMultiplier = 0.5 + handStrength * 1.5;
    const potBasedRaise = potSize * strengthMultiplier * this.aggressionFactor;
    
    // 取较大值，但不超过筹码
    const raiseAmount = Math.max(baseRaise, potBasedRaise);
    
    return Math.min(Math.floor(raiseAmount), player.chips);
  }

  private getValidActions(state: GameState, player: SeatPlayer): PlayerActionType[] {
    const actions: PlayerActionType[] = ['fold'];
    const toCall = state.currentBet - player.currentBet;

    if (toCall === 0) {
      actions.push('check');
    }

    if (player.chips > toCall) {
      if (toCall > 0) actions.push('call');
      actions.push('raise');
    }

    if (player.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  }
}
